/**
 * MultiSelect — the chip-combobox (user steer 2026-07-13): selected values as
 * removable chips INSIDE the field, typing filters the option list
 * (autocomplete), the dropdown is a checkbox list with an "N selected" header.
 * Keyboard-first: type→filter · ↵ toggles the top match · ⌫ on an empty query
 * removes the last chip · Esc closes. Commits per toggle (optimistic — the
 * grid-cell philosophy, not a dirty form).
 *
 * Options come from the semantic layer (field_meta options / options_source
 * catalogs) — the control never owns a vocabulary.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cx } from './cx'
import { useOverlayEscapeLayer } from './OverlayPortal'
import s from './MultiSelect.module.css'

/**
 * One option, carrying its own availability (#1075) — the same payload
 * `Select` reads, because a vocabulary has one shape.
 *
 * `selectable: false` ⇒ present and labelled, closed to a NEW choice. A value
 * already held stays a chip with its real label and can still be REMOVED:
 * archiving a definition retires it, it does not invalidate the records that
 * already carry it. Absent reads as selectable.
 */
export interface MSOption { value: string; label: string; selectable?: boolean }

/**
 * The props <Field> clones onto its single child (UX-04).
 *
 * MultiSelect's root is a `<div>`, so React accepted `id` / `aria-describedby` /
 * `aria-invalid` / `aria-required` from the clone, dropped them on the floor —
 * they were not in the prop type and nothing forwarded them — and the field was
 * left with a `<label htmlFor>` pointing at an element that did not exist. The
 * label looked associated and named nothing.
 *
 * They are forwarded to the INPUT rather than the wrapper, because the input is
 * what a user focuses and what a screen reader announces; `id` in particular has
 * to land there or `htmlFor` still misses.
 */
export function MultiSelect({
  value, options, onChange, placeholder = 'Search…', testid, listLayout = 'overlay',
  id, 'aria-describedby': describedBy, 'aria-invalid': invalid, 'aria-required': required,
  'aria-label': ariaLabel,
}: {
  value: string[]
  options: MSOption[]
  onChange: (next: string[]) => void
  placeholder?: string
  testid?: string
  /**
   * WHERE THE OPEN LIST GOES (#2375 repair).
   *
   * `overlay` (the default) floats the list over whatever follows the field —
   * right for a form row, where the next field can wait.
   *
   * `inline` puts the list IN FLOW, so it pushes what follows down instead of
   * covering it. A surface whose commit button sits directly under the field —
   * the column filter editor's "Apply filter" — needs this: the floating list
   * sat on top of Apply, and a person had to click somewhere neutral to close
   * it before they could apply (the portal's e2e helper did exactly that).
   * In flow, Apply is laid out AFTER the list and cannot be under it.
   */
  listLayout?: 'overlay' | 'inline'
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-required'?: boolean
  'aria-label'?: string
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hi, setHi] = useState(-1)              // arrow-key highlight; -1 = none
  const wrap = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  /* Set while Escape hands focus back to the input, so the input's
     open-on-focus does not re-open the list Escape just closed. */
  const quietFocus = useRef(false)
  /**
   * The listbox's id, and the stem of every option's id (#1466 row 6).
   *
   * Two defects, one line. It used to be `id ? `${id}-listbox` : undefined`, so
   * a MultiSelect the caller did not give an `id` emitted
   * `aria-controls={undefined}` — a combobox announcing that it controls
   * nothing. `useId()` is the fallback, because the relationship between this
   * input and this list is the component's own fact and not the caller's.
   *
   * And the options now carry ids at all, which is what makes
   * `aria-activedescendant` below possible: `hi` moved a highlight and scrolled
   * to it while announcing nothing, so an arrow key was a silent move for a
   * screen-reader user. `OptionPicker` and `CommandPalette` use the same
   * `${listId}-option-${index}` scheme.
   */
  const listId = `${useId()}-listbox`

  /* Closing on an outside press. An OVERLAY list closes on `mousedown`, as it
     always has. An INLINE list closes on `click` — after the press is over —
     because collapsing it moves everything below it: on `mousedown` the list
     folded away mid-press, "Apply filter" jumped up by the list's height, the
     `mouseup` landed on whatever had moved under the pointer, and the click
     never reached Apply. */
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) { setOpen(false); setQ('') }
    }
    const type = listLayout === 'inline' ? 'click' : 'mousedown'
    document.addEventListener(type, onDoc)
    return () => document.removeEventListener(type, onDoc)
  }, [listLayout])

  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options])
  const chips = value.map((v) => byValue.get(v) ?? { value: v, label: v })
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase()
    const pool = t ? options.filter((o) => o.label.toLowerCase().includes(t)) : options
    return pool.filter((o) => !value.includes(o.value)).concat(pool.filter((o) => value.includes(o.value)))
      .filter((o, i, a) => a.indexOf(o) === i)
  }, [options, q, value])

  const toggle = (v: string) => {
    // Removal is always allowed; ADDING an unavailable value is not. The check
    // is here rather than in the list so keyboard ↵ and mouse take the same
    // path — a control that refuses on click and accepts on Enter is worse than
    // one that refuses nowhere.
    if (!value.includes(v) && byValue.get(v)?.selectable === false) return
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
    setQ('')
    setHi(-1)
    input.current?.focus()
  }
  const remove = (v: string) => onChange(value.filter((x) => x !== v))

  return (
    <div ref={wrap} className={s.wrap} data-testid={testid} data-open={open || undefined}
      data-list-layout={listLayout}
      /* ESCAPE CLOSES THE LIST, AND ONLY THE LIST (#2375 repair). On the wrapper,
         not the input, so it also answers from an option a person tabbed to.
         Handled only while open: a closed MultiSelect lets Escape through to
         whatever holds it. `stopPropagation` + `preventDefault` so a bubble-
         phase host listener (a drawer, a sheet) does not ALSO close; the
         capture-phase ones — `AnchoredSurface`, a `Modal` — stand down because
         the open list registered an escape layer (see `MenuLayer`). Focus goes
         back to the input, because the option that held it is about to unmount
         and a keyboard user would otherwise be dropped on `<body>`. */
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || !open) return
        e.preventDefault()
        e.stopPropagation()
        setOpen(false); setQ(''); setHi(-1)
        if (document.activeElement !== input.current) {
          quietFocus.current = true
          input.current?.focus()
          quietFocus.current = false
        }
      }}>
      <div className={s.field} onClick={() => { setOpen(true); input.current?.focus() }}>
        {chips.map((c) => (
          <span key={c.value} className={s.chip}>
            {c.label}
            <button type="button" aria-label={`Remove ${c.label}`} onClick={(e) => { e.stopPropagation(); remove(c.value) }}>×</button>
          </span>
        ))}
        <input ref={input} value={q} placeholder={chips.length === 0 ? placeholder : ''}
          id={id} aria-describedby={describedBy} aria-invalid={invalid} aria-required={required}
          aria-label={ariaLabel}
          // The combobox contract, so the announcement matches the behaviour:
          // this input owns a multi-selectable listbox and says whether it is open.
          role="combobox" aria-expanded={open} aria-autocomplete="list"
          aria-controls={listId}
          aria-activedescendant={open && hi >= 0 && filtered[hi] ? `${listId}-option-${hi}` : undefined}
          onFocus={() => { if (!quietFocus.current) setOpen(true) }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(-1) }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !q && value.length > 0) remove(value[value.length - 1]!)
            else if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(filtered.length - 1, h + 1)) }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(-1, h - 1)) }
            else if (e.key === 'Enter') {
              e.preventDefault()
              const pick = (hi >= 0 && filtered[hi]) || filtered.find((o) => !value.includes(o.value)) || filtered[0]
              if (pick && open) toggle(pick.value)
            }
          }} />
        <span className={s.caret} aria-hidden onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); if (!open) input.current?.focus() }}><ChevronDown size={15} /></span>
      </div>
      {open && (
        <MenuLayer>
        <div className={s.menu} role="listbox" aria-multiselectable id={listId}>
          <div className={s.count}>{value.length} selected</div>
          {filtered.map((o, i) => {
            const on = value.includes(o.value)
            const closed = o.selectable === false && !on
            return (
              <button type="button" key={o.value} id={`${listId}-option-${i}`} role="option" aria-selected={on}
                className={cx(s.opt, i === hi && s.optHi)}
                data-hi={i === hi || undefined}
                data-unavailable={closed || undefined}
                disabled={closed}
                ref={(el) => { if (i === hi && el) el.scrollIntoView({ block: 'nearest' }) }}
                onMouseMove={() => { if (hi !== i) setHi(i) }}
                onClick={() => toggle(o.value)}>
                <span className={cx(s.box, on && s.boxOn)}>{on ? '✓' : ''}</span>
                {o.label}
                {o.selectable === false && <span className={s.marker}>Archived</span>}
              </button>
            )
          })}
          {filtered.length === 0 && <div className={s.none}>No matches.</div>}
        </div>
        </MenuLayer>
      )}
    </div>
  )
}

/**
 * The open list claims Escape for as long as it is mounted.
 *
 * A host surface — `AnchoredSurface`, `Modal` — listens for Escape on the
 * document in the CAPTURE phase, which runs before any handler in here can
 * stop it. So stopping propagation is not enough: the column filter editor
 * closed WHOLE on the first Escape and the list went with it. Registering an
 * escape layer is how a nested layer tells the surface around it "this one is
 * mine"; `DatePicker`'s calendar and `Lookup`'s popover already do the same.
 * A component rather than a call in `MultiSelect`, so the claim lives exactly
 * as long as the list does.
 */
function MenuLayer({ children }: { children: ReactNode }) {
  useOverlayEscapeLayer()
  return <>{children}</>
}
